import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Slider from "react-slick";
import { getCountdown } from "../helper/Countdown";
import toast from "react-hot-toast";
import { Helmet } from "react-helmet-async";

const ProductDetailsTwo = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [mainImage, setMainImage] = useState("");
  const [productImages, setProductImages] = useState([]);
  const [quantity, setQuantity] = useState(1);

  const [sku, setSku] = useState("");
  const [rating, setRating] = useState(0);
  const [reviews, setReviews] = useState(0);
  const [shipping, setShipping] = useState(0);
  const [tags, setTags] = useState([]);

  /* ---------------- ADD TO CART (UNCHANGED) ---------------- */
  const handleAddToCart = () => {
    const isLoggedIn = sessionStorage.getItem("isLoggedIn");
    if (isLoggedIn !== "true") {
      toast.error("Please login first!");
      navigate("/login");
      return;
    }

    let cart = JSON.parse(sessionStorage.getItem("cart")) || [];
    const existing = cart.find((i) => i.id === product.id);

    existing
      ? (existing.quantity += quantity)
      : cart.push({ ...product, quantity });

    sessionStorage.setItem("cart", JSON.stringify(cart));
    toast.success(`${product.name} added to cart`);
  };

  /* ---------------- TAGS ---------------- */
  useEffect(() => {
    if (!product?.name) return;
    const base = product.name.split(" ").filter((w) => w.length > 4);
    const extra = ["Best Seller", "High Quality", "Fast Shipping"];
    setTags([...base, ...extra].slice(0, 4));
  }, [product]);

  /* ---------------- DATA LOAD ---------------- */
  useEffect(() => {
    fetch("/assets/data/hp-products.json")
      .then((r) => r.json())
      .then((data) => {
        const found = data.find((p) => p.id === parseInt(id));
        if (!found) return;

        setProduct(found);
        setMainImage(found.imageUrl);
        setProductImages(
          [found.imageUrl, ...(found.additionalImages || [])].filter(Boolean)
        );

        setSku(
          "SY-" + Math.random().toString(36).substring(2, 6).toUpperCase()
        );
        setRating((Math.random() * 1 + 4).toFixed(1));
        setReviews(Math.floor(Math.random() * 5000 + 800));
        setShipping(Math.random() < 0.5 ? 0 : 10);
      });
  }, [id]);

  /* ---------------- COUNTDOWN ---------------- */
  const [timeLeft, setTimeLeft] = useState(getCountdown());
  useEffect(() => {
    const t = setInterval(() => setTimeLeft(getCountdown()), 1000);
    return () => clearInterval(t);
  }, []);

  if (!product) return <div className="text-center py-80">Loading…</div>;

  const days = Math.ceil(
    (new Date(new Date().getFullYear(), 11, 31) - new Date()) / 864e5
  );

  return (
    <>
      <Helmet>
        <title>{product.name} | Awaneez</title>
      </Helmet>

      <section className="sx-product">
        <div className="sx-wrap">
          <div className="sx-grid">
            {/* IMAGES */}
            <div className="sx-images">
              <div className="sx-main-img">
                <img src={mainImage} alt={product.name} />
              </div>

              <div className="sx-thumbs">
                {productImages.map((img, i) => (
                  <img
                    key={i}
                    src={img}
                    alt=""
                    onClick={() => setMainImage(img)}
                    className={mainImage === img ? "active" : ""}
                  />
                ))}
              </div>
            </div>

            {/* INFO */}
            <div className="sx-info">
              <span className="sx-category">
                {product.category.replace(/-/g, " ")}
              </span>

              <h1>{product.name}</h1>

              <div className="sx-rating">
                ⭐ {rating} ({reviews.toLocaleString()} reviews)
              </div>

              <div className="sx-price">
                <span>${product.price}</span>
                <small>
                  <del>${(product.price * 1.2).toFixed(2)}</del>
                </small>
              </div>

              <div className="sx-stock">
                ✔ In Stock ({product.stock} available)
              </div>

              <div className="sx-qty">
                <button onClick={() => setQuantity((q) => Math.max(1, q - 1))}>
                  −
                </button>
                <span>{quantity}</span>
                <button onClick={() => setQuantity((q) => q + 1)}>+</button>
              </div>

              <button className="sx-cart" onClick={handleAddToCart}>
                Add to Cart
              </button>

              <div className="sx-tags">
                {tags.map((t, i) => (
                  <span key={i}>{t}</span>
                ))}
              </div>

              <div className="sx-trust">
                <img src="/assets/images/thumbs/payment-method.png" alt="" />
                <p>Secure checkout · Fast delivery</p>
              </div>

              <div className="sx-offer">⏳ Offer ends in {days} days</div>
            </div>
          </div>

          {/* DESCRIPTION */}
          <div className="sx-desc">
            <h3>Product Description</h3>
            <p>{product.description}</p>

            {product.compatibility && (
              <>
                <h4>Compatibility</h4>
                <p>{product.compatibility}</p>
              </>
            )}
          </div>
        </div>

        {/* CSS */}
        <style>{`
          .sx-product {
            background:#f6f7fb;
            padding:70px 0;
          }
          .sx-wrap {
            max-width:1200px;
            margin:auto;
            padding:0 20px;
          }
          .sx-grid {
            display:grid;
            grid-template-columns:1.1fr 1fr;
            gap:40px;
            background:#fff;
            padding:40px;
          }
          .sx-main-img img {
            width:100%;
            max-height:420px;
            object-fit:contain;
          }
          .sx-thumbs {
            display:flex;
            gap:10px;
            margin-top:14px;
          }
          .sx-thumbs img {
            width:70px;
            height:70px;
            object-fit:contain;
            border:1px solid #ddd;
            cursor:pointer;
          }
          .sx-thumbs img.active {
            border:2px solid #111;
          }
          .sx-category {
            text-transform:uppercase;
            font-size:12px;
            color:#777;
          }
          .sx-info h1 {
            font-size:26px;
            margin:10px 0;
          }
          .sx-rating {
            color:#555;
            font-size:14px;
          }
          .sx-price span {
            font-size:28px;
            font-weight:800;
          }
          .sx-price del {
            color:#999;
            margin-left:10px;
          }
          .sx-stock {
            margin:14px 0;
            color:green;
            font-weight:600;
          }
          .sx-qty {
            display:flex;
            align-items:center;
            gap:14px;
            margin-bottom:16px;
          }
          .sx-qty button {
            width:36px;
            height:36px;
            border:1px solid #ccc;
            background:#fff;
          }
          .sx-cart {
            width:100%;
            padding:14px;
            background:#111;
            color:#fff;
            font-weight:700;
            border:none;
            cursor:pointer;
          }
          .sx-tags span {
            display:inline-block;
            margin:10px 6px 0 0;
            padding:6px 12px;
            border:1px solid #ddd;
            font-size:12px;
          }
          .sx-trust {
            margin-top:18px;
            font-size:13px;
            color:#555;
          }
          .sx-offer {
            margin-top:12px;
            font-size:13px;
            color:#b00020;
          }
          .sx-desc {
            background:#fff;
            margin-top:30px;
            padding:30px;
          }
          @media(max-width:900px){
            .sx-grid{
              grid-template-columns:1fr;
            }
          }
          .sx-desc h4 {
              font-size: 26px;
              margin-top: 10px;
          }
          .sx-desc h3 {
              font-size: 26px;
              margin-top: 10px;
          }
        `}</style>
      </section>
    </>
  );
};

export default ProductDetailsTwo;
